import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import {
	Controller,
	type DefaultValues,
	type FieldError,
	useForm,
	useWatch
} from 'react-hook-form'
import toast from 'react-hot-toast'
import { FloatingInput } from '@/components/commons/floating-input'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle
} from '@/components/ui/drawer'
import { AlertFillCircleIcon } from '@/components/ui/icons'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import type { ComboboxOption } from '@/features/marquee/components/combobox-field'
import { FloatingComboboxField } from '@/features/marquee/components/floating-combobox-field'
import { handleLocationError } from '@/features/marquee/components/location-toasts'
import {
	LocationFormSchema,
	type LocationFormValues
} from '@/features/marquee/form-schemas'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { marketsQueryOptions } from '@/features/marquee/query-options'
import {
	type LocationErrorCode,
	locationErrorCode,
	locationErrorLabelKey,
	operatorValidationErrors,
	supportedTimeZones,
	timeZoneOffsetLabel
} from '@/features/marquee/utils'
import { getCountryNameFromCode } from '@/helpers/utils'
import {
	createLocation,
	type MarqueeGeo,
	type MarqueeLocation,
	type MarqueeLocationUpdate,
	updateLocation
} from '@/sdk'
import { type TKey, useTranslation } from '@/translations/i18n'

export type LocationDialogTarget =
	| { mode: 'create' }
	| { mode: 'edit'; location: MarqueeLocation }

type FormField = keyof LocationFormValues

/** Request field → form field, for the messages of a 422 `validation-error`. */
const REQUEST_FIELD_TO_FORM_FIELD: Record<string, FormField> = {
	name: 'name',
	geo_id: 'marketId',
	time_zone: 'timeZone'
}

/**
 * The conflicts the person filling the form can act on, shown on the field
 * they are about rather than in a toast. Every other code is a toast.
 */
const ERROR_CODE_TO_FORM_FIELD: Partial<Record<LocationErrorCode, FormField>> =
	{
		'location-name-taken': 'name',
		'location-in-use': 'marketId'
	}

const EMPTY: DefaultValues<LocationFormValues> = {
	name: '',
	country: '',
	marketId: '',
	timeZone: ''
}

/**
 * The location carries only a market reference (`MarqueeMarketRef`: id and
 * name), so the country comes from `/markets`, looked up by market id: right
 * away when that list is already cached, otherwise empty until it arrives (see
 * the effect in the dialog). The PATCH never reads it.
 */
const toFormValues = (
	location: MarqueeLocation,
	markets?: MarqueeGeo[]
): LocationFormValues => ({
	name: location.name,
	country: markets?.find((m) => m.id === location.market.id)?.country ?? '',
	marketId: location.market.id,
	timeZone: location.time_zone
})

/** The PATCH body: only what changed, as the API asks. */
const changedFields = (
	location: MarqueeLocation,
	values: LocationFormValues
): MarqueeLocationUpdate => {
	const before = toFormValues(location)
	const body: MarqueeLocationUpdate = {}
	if (values.name.trim() !== before.name) {
		body.name = values.name.trim()
	}
	if (values.marketId !== before.marketId) {
		body.geo_id = values.marketId
	}
	if (values.timeZone !== before.timeZone) {
		body.time_zone = values.timeZone
	}
	return body
}

interface LocationDialogProps {
	target: LocationDialogTarget
	onClose: () => void
	/**
	 * Edit only: hands the location to the page's deactivate confirmation.
	 * Left out when it cannot be deactivated (already deactivated, or the
	 * viewer lacks the permission), which hides the button.
	 */
	onDeactivate?: () => void
	/**
	 * Add only: the saved location, once the lists have refetched, so a caller
	 * can pick it straight away (the event form's "Add new").
	 */
	onCreated?: (location: MarqueeLocation) => void
}

/**
 * Add / edit location form (`createLocation` / `updateLocation`), one dialog
 * for both modes.
 *
 * Country only narrows the market list (it is the market's own `country`) and
 * is never sent. Once events use the location the parent market is locked,
 * with the reason, and the country goes with it; a time zone change on such a
 * location is warned about, since it moves the shown times of those events.
 *
 * Controlled by the page and mounted only while open, keyed on the target, so
 * every open starts from the row it edits (or a blank form). Both writes
 * refetch every locations list, so the event form sees the change.
 *
 * Desktop → `Dialog`, mobile → `Drawer`; the form body is shared.
 */
function LocationDialog({
	target,
	onClose,
	onDeactivate,
	onCreated
}: LocationDialogProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')
	const queryClient = useQueryClient()
	const location = target.mode === 'edit' ? target.location : undefined
	const eventCount = location?.event_count ?? 0
	const isMarketLocked = eventCount > 0
	const dialogContentRef = useRef<HTMLDivElement>(null)
	const drawerContentRef = useRef<HTMLDivElement>(null)

	const { data: markets = [], isLoading: isLoadingMarkets } = useQuery(
		marketsQueryOptions()
	)

	// Read once, at mount: with `/markets` already cached (any open after the
	// first) the country is part of the form's defaults from the start, rather
	// than reset in after mount, which the form does not pick up then.
	const [defaultValues] = useState<DefaultValues<LocationFormValues>>(() =>
		location
			? toFormValues(
					location,
					queryClient.getQueryData(marketsQueryOptions().queryKey)
				)
			: EMPTY
	)

	const {
		register,
		control,
		handleSubmit,
		setError,
		setValue,
		getValues,
		resetField,
		formState: { errors, isDirty, isValid }
	} = useForm<LocationFormValues>({
		resolver: zodResolver(LocationFormSchema),
		mode: 'onChange',
		defaultValues
	})
	const country = useWatch({ control, name: 'country' })

	// First open, `/markets` not cached yet: the country arrives after mount
	// and goes in as the field's default rather than a change (country is
	// never sent, so filling it in must not enable Save).
	const locationCountry = markets.find(
		(m) => m.id === location?.market.id
	)?.country
	useEffect(() => {
		if (locationCountry && locationCountry !== defaultValues.country) {
			resetField('country', { defaultValue: locationCountry })
		}
	}, [locationCountry, defaultValues.country, resetField])
	const timeZone = useWatch({ control, name: 'timeZone' })

	const showTimeZoneWarning =
		location !== undefined &&
		isMarketLocked &&
		timeZone !== location.time_zone

	// `country` is the market's ISO alpha-2 code ("US"): it stays the value,
	// the label is its translated name (the code itself for one the list does
	// not carry), and the options sort by what is shown.
	const countryOptions = useMemo<ComboboxOption[]>(
		() =>
			[...new Set(markets.map((m) => m.country))]
				.filter((code): code is string => Boolean(code))
				.map((code) => {
					const countryName = getCountryNameFromCode(code)
					return { value: code, label: countryName ?? code }
				})
				.sort((a, b) => a.label.localeCompare(b.label)),
		[markets]
	)

	const marketOptions = useMemo<ComboboxOption[]>(
		() =>
			markets
				.filter(
					(m) =>
						!country ||
						m.country === country ||
						m.id === location?.market.id
				)
				.map((m) => ({ value: m.id, label: m.name })),
		[markets, country, location?.market.id]
	)

	// The saved and the current zone stay options even if this browser does not
	// list them: a market's zone is the backend's spelling, not the browser's.
	const timeZoneOptions = useMemo<ComboboxOption[]>(() => {
		const zones = supportedTimeZones()

		console.log(location?.time_zone, timeZone)
		for (const zone of [location?.time_zone, timeZone]) {
			if (zone && !zones.includes(zone)) {
				zones.unshift(zone)
			}
		}
		return zones.map((zone) => ({
			value: zone,
			label: `${zone} (${timeZoneOffsetLabel(zone)})`
		}))
	}, [location, timeZone])

	// What the last market filled in, to tell it from the admin's own pick.
	const prefilledZone = useRef('')

	/**
	 * Add only: a picked market fills in its zone (`null` for the many markets
	 * with none, and then the admin picks by hand). Only ever over an empty
	 * field or an earlier prefill, so a zone chosen by hand is never replaced;
	 * on edit the saved zone is left alone, changing it moves the shown times.
	 */
	const prefillTimeZone = (marketId: string) => {
		const current = getValues('timeZone')
		if (location || (current && current !== prefilledZone.current)) {
			return
		}
		const zone = markets.find((m) => m.id === marketId)?.time_zone ?? ''
		prefilledZone.current = zone
		setValue('timeZone', zone, { shouldDirty: true, shouldValidate: true })
	}

	const save = useMutation({
		mutationFn: async (values: LocationFormValues) => {
			if (location) {
				await updateLocation({
					path: { locationId: location.id },
					body: changedFields(location, values)
				})
				return
			}
			const response = await createLocation({
				body: {
					name: values.name.trim(),
					geo_id: values.marketId,
					time_zone: values.timeZone
				}
			})
			return response.data
		},
		onSuccess: async (created, values) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.locations.all()
			})
			toast.success(
				location
					? t('marquee:locations_updated_toast', {
							name: values.name.trim()
						})
					: t('marquee:locations_added_toast')
			)
			if (created) {
				onCreated?.(created)
			}
			onClose()
		},
		onError: (error) => {
			const fieldMessages = operatorValidationErrors(error)
			if (fieldMessages) {
				let shownOnField = false
				for (const [requestField, messages] of Object.entries(
					fieldMessages
				)) {
					const field = REQUEST_FIELD_TO_FORM_FIELD[requestField]
					if (field && messages[0]) {
						// The backend's own sentence, shown as is.
						setError(field, {
							type: 'server',
							message: messages[0]
						})
						shownOnField = true
					}
				}
				if (!shownOnField) {
					toast.error(
						Object.values(fieldMessages).flat()[0] ??
							t('errors:default')
					)
				}
				return
			}
			const code = locationErrorCode(error)
			const field = code && ERROR_CODE_TO_FORM_FIELD[code]
			if (code && field) {
				setError(field, {
					type: 'code',
					message: locationErrorLabelKey(code)
				})
				return
			}
			handleLocationError(error)
		}
	})

	const onSubmit = handleSubmit((values) => save.mutate(values))

	/** Zod and code errors carry a key; a server field message is already text. */
	const errorText = (error?: FieldError) => {
		if (!error?.message) {
			return undefined
		}
		return error.type === 'server'
			? error.message
			: t(error.message as TKey)
	}

	const nameHint =
		location && eventCount === 0
			? t('marquee:locations_name_no_events_hint')
			: t('marquee:locations_name_add_hint')

	const title = location
		? t('marquee:locations_edit_title')
		: t('marquee:locations_add')
	const description = location
		? undefined
		: t('marquee:locations_add_description')

	const fields = (container: RefObject<HTMLElement | null>) => (
		<div className='flex flex-col gap-8'>
			<div className='flex flex-col gap-3'>
				<Label htmlFor='name'>
					{t('marquee:locations_name_label')}
				</Label>
				<FloatingInput
					errorMessage={errorText(errors.name)}
					helpText={nameHint}
					inputSize='lg'
					label={t('marquee:locations_name_placeholder')}
					type='text'
					{...register('name')}
				/>
			</div>
			{/* Kept while `/markets` loads, so the form does not jump when the
			    list arrives; hidden only when no market carries a country. */}
			{!isMarketLocked &&
				(isLoadingMarkets || countryOptions.length > 0) && (
					<Controller
						control={control}
						name='country'
						render={({ field }) => (
							<div className='flex flex-col gap-3'>
								<Label>
									{t('marquee:locations_country_label')}
								</Label>
								<FloatingComboboxField
									container={container}
									emptyLabel={t('marquee:combobox_empty')}
									label={t(
										'marquee:locations_country_placeholder'
									)}
									loading={isLoadingMarkets}
									onChange={(value) => {
										field.onChange(value ?? '')
										// A market from another country no longer
										// fits, so the pick is cleared with it.
										setValue('marketId', '', {
											shouldDirty: true,
											shouldValidate: true
										})
										prefillTimeZone('')
									}}
									options={countryOptions}
									searchPlaceholder={t(
										'marquee:combobox_search_placeholder'
									)}
									value={field.value || null}
								/>
							</div>
						)}
					/>
				)}
			<Controller
				control={control}
				name='marketId'
				render={({ field }) => (
					<div className='flex flex-col gap-3'>
						<Label
							className={
								isMarketLocked ? 'text-grey-700' : undefined
							}
						>
							{t('marquee:locations_market_label')}
						</Label>
						<FloatingComboboxField
							container={container}
							disabled={isMarketLocked}
							emptyLabel={t('marquee:combobox_empty')}
							errorMessage={errorText(errors.marketId)}
							label={t('marquee:locations_market_placeholder')}
							loading={isLoadingMarkets}
							onChange={(value) => {
								field.onChange(value ?? '')
								prefillTimeZone(value ?? '')
							}}
							options={marketOptions}
							searchPlaceholder={t(
								'marquee:combobox_search_placeholder'
							)}
							value={field.value || null}
						/>
						{isMarketLocked && (
							<div className='flex items-start gap-3 rounded-2xl bg-grey-100 p-4'>
								<AlertFillCircleIcon
									className='mt-0.5 shrink-0 text-red-700'
									size={20}
								/>
								<div className='flex flex-col gap-1'>
									<p className='font-medium text-grey-1000 text-md/5'>
										{t(
											'marquee:locations_market_locked_title',
											{ count: eventCount }
										)}
									</p>
									<p className='text-neutral-500 text-sm/4.5'>
										{t(
											'marquee:locations_market_locked_description'
										)}
									</p>
								</div>
							</div>
						)}
					</div>
				)}
			/>
			<Controller
				control={control}
				name='timeZone'
				render={({ field }) => (
					<div className='flex flex-col gap-3'>
						<Label>{t('marquee:locations_time_zone_label')}</Label>
						<FloatingComboboxField
							container={container}
							emptyLabel={t('marquee:combobox_empty')}
							errorMessage={errorText(errors.timeZone)}
							label={t('marquee:locations_time_zone_placeholder')}
							onChange={(value) => field.onChange(value ?? '')}
							options={timeZoneOptions}
							searchPlaceholder={t(
								'marquee:combobox_search_placeholder'
							)}
							value={field.value || null}
						/>
						{showTimeZoneWarning && (
							<p className='flex items-start gap-2 text-red-700 text-sm'>
								<AlertFillCircleIcon
									className='mt-0.5 shrink-0'
									size={16}
								/>
								{t(
									'marquee:locations_time_zone_change_warning',
									{ count: eventCount }
								)}
							</p>
						)}
					</div>
				)}
			/>
		</div>
	)

	const actions = (
		<>
			<Button onClick={onClose} type='button' variant='tertiary'>
				{t('marquee:cancel')}
			</Button>
			<div className='flex items-center gap-3'>
				{location && onDeactivate && (
					<Button
						onClick={onDeactivate}
						type='button'
						variant='secondaryOutline'
					>
						{t('marquee:locations_action_deactivate')}
					</Button>
				)}
				<Button
					// Add waits for a complete form, edit for an actual change,
					// both as in the design.
					disabled={
						save.isPending || (location ? !isDirty : !isValid)
					}
					onClick={onSubmit}
					type='button'
					variant='primary'
				>
					{location
						? t('marquee:locations_save')
						: t('marquee:locations_add')}
					{save.isPending && <Spinner size={16} />}
				</Button>
			</div>
		</>
	)

	const onOpenChange = (open: boolean) => {
		if (!open) {
			onClose()
		}
	}

	if (isDesktop) {
		return (
			<Dialog onOpenChange={onOpenChange} open>
				{/* The body scrolls and the footer stays put: `DialogContent` is a
				    grid capped at the viewport, so its scrolling row needs an
				    explicit `minmax(0,1fr)`. */}
				<DialogContent
					className='max-w-140 grid-rows-[minmax(0,1fr)_auto]'
					ref={dialogContentRef}
				>
					<DialogBody className='gap-8'>
						<div className='flex flex-col gap-2 text-center'>
							<DialogTitle>{title}</DialogTitle>
							{description && (
								<DialogDescription className='text-neutral-500'>
									{description}
								</DialogDescription>
							)}
						</div>
						{fields(dialogContentRef)}
					</DialogBody>
					<DialogFooter className='flex flex-row justify-between'>
						{actions}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer onOpenChange={onOpenChange} open>
			<DrawerContent ref={drawerContentRef}>
				<DrawerHeader className='pb-4'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{title}
					</DrawerTitle>
					{description && (
						<DrawerDescription className='text-neutral-500'>
							{description}
						</DrawerDescription>
					)}
				</DrawerHeader>
				{/* The drawer is capped at 80vh, so the fields take the leftover
				    space and scroll rather than pushing the footer off-screen. */}
				<div className='min-h-0 flex-1 overflow-y-auto px-4 pb-1'>
					{fields(drawerContentRef)}
				</div>
				<DrawerFooter className='flex-row justify-between'>
					{actions}
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}

export { LocationDialog }
